import { SpaceNotFoundError, type SyncManager } from '@wiztime/sync';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toastInfo } from '../../shell/toastStore';
import { Button, ButtonVariant } from '../../ui/Button';
import { DeriveProgress } from './GenerateFlow';
import { checkPassphraseText, PassphraseCheckKind, type PassphraseCheck } from './passphraseCheck';
import styles from './Sync.module.css';
import { syncFailureKey } from './syncErrors';
import { toConfigUrl } from './urls';

interface JoinFlowProps {
  readonly manager: SyncManager;
  readonly serverUrl: string;
  readonly onBack: () => void;
  /** Joined an existing space (not created a new one); its first sync has just started. */
  readonly onJoined?: () => void;
}

/** The sentence under the text box for the current input. */
export function usePassphraseMessage(check: PassphraseCheck): string {
  const { t } = useTranslation();
  switch (check.kind) {
    case PassphraseCheckKind.Empty:
      return t('sync.join.hint');
    case PassphraseCheckKind.WordCount:
      return t('sync.join.wordCount', { count: check.count });
    case PassphraseCheckKind.UnknownWord:
      return t('sync.join.unknownWord', { position: check.position ?? 0, word: check.word ?? '' });
    case PassphraseCheckKind.Checksum:
      return t('sync.join.checksum');
    case PassphraseCheckKind.Valid:
      return t('sync.join.valid');
  }
}

/** Join an existing space with its passphrase, typed or pasted, validated as the user types. */
export function JoinFlow({ manager, serverUrl, onBack, onJoined }: JoinFlowProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const check = useMemo(() => checkPassphraseText(text), [text]);
  const message = usePassphraseMessage(check);
  const valid = check.kind === PassphraseCheckKind.Valid;
  const bad = check.kind === PassphraseCheckKind.UnknownWord || check.kind === PassphraseCheckKind.Checksum;

  const run = async (create: boolean): Promise<void> => {
    setBusy(true);
    setFailure(null);
    setNotFound(false);
    const input = { serverUrl: toConfigUrl(serverUrl), passphrase: check.normalized ?? text };
    try {
      if (create) await manager.enableSync(input);
      else {
        await manager.joinSync(input);
        onJoined?.();
      }
      toastInfo(t(create ? 'sync.generate.enabled' : 'sync.join.joined'));
    } catch (error) {
      if (error instanceof SpaceNotFoundError) setNotFound(true);
      else {
        console.warn('[sync] joining failed', error);
        setFailure(t(syncFailureKey(error)));
      }
      setBusy(false);
    }
  };

  return (
    <div className={styles.flow} data-testid="sync-join">
      <h3 className={styles.flowTitle}>{t('sync.join.title')}</h3>
      <label htmlFor="sync-passphrase" className={styles.muted}>
        {t('sync.join.intro')}
      </label>
      <textarea
        id="sync-passphrase"
        className={styles.passphraseInput}
        rows={3}
        value={text}
        disabled={busy}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        aria-invalid={bad}
        aria-describedby="sync-passphrase-message"
        placeholder={t('sync.join.placeholder')}
        data-testid="passphrase-input"
        onChange={(event) => {
          setText(event.target.value);
          setNotFound(false);
          setFailure(null);
        }}
      />
      <p
        id="sync-passphrase-message"
        className={bad ? styles.errorText : valid ? styles.good : styles.muted}
        aria-live="polite"
        data-testid="passphrase-message"
        data-kind={check.kind}
      >
        {message}
      </p>
      {busy ? <DeriveProgress /> : null}
      {failure === null ? null : (
        <p className={styles.errorText} role="alert" data-testid="sync-failure">
          {failure}
        </p>
      )}
      {notFound ? (
        <div className={styles.notice} role="alert" data-testid="sync-not-found">
          <p>{t('sync.join.notFound')}</p>
          <p className={styles.muted}>{t('sync.join.notFoundHint')}</p>
          <Button onClick={() => void run(true)} data-testid="sync-create-instead">
            {t('sync.join.createInstead')}
          </Button>
        </div>
      ) : null}
      <div className={styles.actions}>
        <Button disabled={busy} onClick={onBack} data-testid="sync-back">
          {t('sync.back')}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          disabled={!valid || busy}
          onClick={() => void run(false)}
          data-testid="sync-join-button"
        >
          {t('sync.join.button')}
        </Button>
      </div>
    </div>
  );
}
